// src/context/NotificationContext.js
import React, { createContext, useState, useCallback, useMemo, useRef } from "react";

export const NotificationContext = createContext();

// ⚠️ A NOTICE ALWAYS HAS WORDS (prod hotfix, 2026-10-05). The operator was
// shown a large red box with nothing in it. The words were there — the box
// had been knocked out of `position: fixed` (see global.css) — but nothing
// here guaranteed them either: a server BODY handed over as the message
// (`{message}`, `{detail}`, `{non_field_errors: [...]}`) is not a valid
// React child, and an empty string is a red box that says nothing. Every
// shape a caller sends is turned into a sentence here, ONCE, and a notice
// that would be empty says so in words.
export const EMPTY_NOTICE_TEXT = {
  error: "Something went wrong, and no reason was given.",
  warning: "The server sent a warning with no text.",
  success: "Done.",
  info: "Done.",
};

const BODY_KEYS = ["message", "detail", "error", "non_field_errors"];

const toWords = (message) => {
  if (message === null || message === undefined || message === false) return "";
  if (typeof message === "string") return message.trim();
  if (typeof message === "number") return String(message);
  if (Array.isArray(message)) {
    return message.map(toWords).filter(Boolean).join(" ");
  }
  if (typeof message === "object") {
    for (const key of BODY_KEYS) {
      const said = toWords(message[key]);
      if (said) return said;
    }
    // A field-keyed body (`{name: ["This field is required."]}`): its
    // sentences, never its keys.
    return Object.values(message).map(toWords).filter(Boolean).join(" ");
  }
  return "";
};

/** What a notice SHOWS for whatever it was handed — never nothing. */
export const noticeContent = (message, type = "error") => {
  // A React node (the folded warning list) is already something to read.
  if (React.isValidElement(message)) return message;
  // An array holding React nodes is also something to read; walking it as
  // a body would descend into the elements' internals (review).
  if (Array.isArray(message) && message.some(React.isValidElement)) return message;
  return toWords(message) || EMPTY_NOTICE_TEXT[type] || EMPTY_NOTICE_TEXT.error;
};

// How long an ordinary notice stays. A notice shown with `{sticky: true}`
// has no timer at all.
export const AUTO_DISMISS_MS = 15000;
// ⚠️ STICKY IS OPT-IN, PER CALL (driver ruling, 2026-10-05). The first cut
// made every `warning` sticky — about 40 call sites, including the
// customer's cart and checkout ("items were adjusted"), which would then
// sit on a shopper's screen until closed. Only the call that asked for it
// stays: today, the Animals grid's role warning.
export const isStickyNotice = (notification) => Boolean(notification && notification.sticky);

export const NotificationProvider = ({ children }) => {
  const [notification, setNotification] = useState(null);
  // Cowork I-02b.2 remainder sweep: the auto-dismiss timer of a PRIOR
  // toast was never cancelled — any toast shown within 15s of the last
  // one was wiped when the stale timer fired. Track and clear it.
  const dismissTimerRef = useRef(null);
  // ⚠️ EVERY NOTICE IS ITS OWN NODE (review round 2 of runs 27/28, the
  // BLOCK): with no key, the next folded notice reused the previous one's
  // <details> — still OPEN — so it stayed held by the CSS `:has(details
  // [open])` rule with its fade paused at the 0% keyframe: an invisible
  // box over the page swallowing taps for 15 seconds. A counter id keys
  // the `.notification` div, so React mounts a fresh one per notice.
  const noticeIdRef = useRef(0);

  // `options.key` names what the notice is ABOUT (e.g. one animal's role
  // warning), so `clearNotification(key)` can take it down when that
  // thing is no longer true — and only then: a notice about something
  // else is never touched.
  const showNotification = useCallback((message, type = "error", options = {}) => {
    console.log('[NOTIFICATION] showNotification called:', { message, type });
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    dismissTimerRef.current = null;
    noticeIdRef.current += 1;
    const key = (options && options.key) || null;
    const sticky = Boolean(options && options.sticky);
    setNotification({ id: noticeIdRef.current, message, type, key, sticky });
    // ⚠️ A STICKY NOTICE STAYS UNTIL IT IS DISMISSED (Cowork run 29). A role warning
    // says something is now inconsistent — the keeper edits a row halfway
    // down the page and the notice sits at the top of it, so a 15-second
    // timer took it away before they had scrolled up to read it. It has
    // its own × ("Dismiss"), a tap anywhere on it still puts it down, and
    // the next notice replaces it. Every other kind keeps its 15 seconds.
    if (!sticky) {
      dismissTimerRef.current = setTimeout(() => setNotification(null), AUTO_DISMISS_MS);
    }
  }, []);

  const hideNotification = useCallback(() => {
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    dismissTimerRef.current = null;
    setNotification(null);
  }, []);

  // Take the notice down ONLY if it is the one about `key` (Cowork run
  // 29): the keeper changed the sex back, so "… is now female" is no
  // longer true — but a notice about another animal, or one with no key,
  // stays exactly where it is.
  const clearNotification = useCallback((key) => {
    if (!key) return;
    setNotification((current) => {
      if (!current || current.key !== key) return current;
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
      return null;
    });
  }, []);

  // HOLD the notice until the keeper dismisses it (review of runs 27/28,
  // O2): a keeper who opened "Show the N records" is READING it, and the
  // 15-second timer took it away mid-list. (Since Cowork run 29 a warning
  // has no timer to cancel; the hold still stops the CSS fade-in from
  // replaying and is kept for any non-warning notice that folds a list.) The timer is cancelled and the
  // fade stops (`.notification--held`); a tap still dismisses it, and the
  // next notice starts its own timer as before.
  const holdNotification = useCallback(() => {
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    dismissTimerRef.current = null;
    setNotification((current) => (
      current && !current.held ? { ...current, held: true } : current));
  }, []);

  const contextValue = useMemo(() => ({
    notification,
    showNotification,
    hideNotification,
    holdNotification,
    clearNotification
  }), [notification, showNotification, hideNotification, holdNotification,
    clearNotification]);

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      {notification && (
        // DISMISSIBLE (Cowork run 26, F2): a warning the server sent is
        // never blocking, and the keeper who has read it can put it down
        // with a tap rather than wait out the 15-second timer.
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          key={notification.id}
          className={`notification ${notification.type}`
            + (notification.held ? ' notification--held' : '')
            + (isStickyNotice(notification) ? ' notification--dismissible' : '')}
          title="Tap to dismiss"
          onClick={hideNotification}
        >
          {isStickyNotice(notification) ? (
            // A notice with no timer has to say how to put it down: a
            // visible × (Cowork run 29). The whole notice still dismisses
            // on a tap, so the button needs no handler of its own beyond
            // that — it is there to be SEEN and to be reachable by keyboard.
            // The content is NOT wrapped: a sentence stays a direct child
            // of `.notification`, exactly as every other notice's is.
            <>
              {noticeContent(notification.message, notification.type)}
              <button
                type="button"
                className="notification__dismiss"
                aria-label="Dismiss"
                onClick={(event) => {
                  event.stopPropagation();
                  hideNotification();
                }}
              >
                <span aria-hidden="true">×</span>
              </button>
            </>
          ) : noticeContent(notification.message, notification.type)}
        </div>
      )}
    </NotificationContext.Provider>
  );
};
